import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Menu, RefreshCw } from 'lucide-react';
import { clearApiKey } from '@/api/client';
import { getHealth } from '@/api/observe';
import { cn } from '@/lib/cn';

interface Props {
  /** Opens the mobile sidebar drawer. Hidden on `md+`. */
  onMenuClick: () => void;
}

// 2026-05-10 — Tony calibration #5 quiet-hours window. Mirrors the
// constants in `notifications.service.ts::isInQuietHours()`. If you tune
// the window on the backend, sync these values too. Europe/London — Intl
// handles BST/GMT transitions automatically without a tz library.
const QUIET_HOURS_START = 22;
const QUIET_HOURS_END   = 8;
function isInQuietHours(now: Date = new Date()): boolean {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour:     'numeric',
    hour12:   false,
  });
  const hour = parseInt(fmt.format(now), 10);
  return hour >= QUIET_HOURS_START || hour < QUIET_HOURS_END;
}

/**
 * Sticky header — shows live overall-health pill and a logout button.
 * The pill polls /health on its own (cheap call, ~1 KB) so even pages
 * that don't query health get the indicator.
 *
 * On mobile (`< md`) a hamburger button opens the off-canvas Sidebar.
 */
export default function HeaderBar({ onMenuClick }: Props) {
  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 10_000,
  });

  // 2026-05-10 — re-evaluate the quiet-hours window every 60s so the pill
  // ticks across the 22:00 / 08:00 BST boundary without a page reload.
  // Cheap (a single Intl format on the current Date).
  const [quietHours, setQuietHours] = useState<boolean>(() => isInQuietHours());
  useEffect(() => {
    const id = setInterval(() => setQuietHours(isInQuietHours()), 60_000);
    return () => clearInterval(id);
  }, []);

  const overall = (() => {
    if (isError) return { tone: 'critical', text: 'API unreachable' };
    if (!data) return { tone: 'info', text: 'connecting…' };
    const m = data.services.mongodb.healthy;
    const r = data.services.redis.healthy;
    if (m === false || r === false) return { tone: 'error', text: 'degraded' };
    if (m === null || r === null)   return { tone: 'warn',  text: 'probing' };
    return { tone: 'ok', text: 'healthy' };
  })();

  return (
    <header className="min-h-12 px-2 sm:px-4 pt-safe border-b border-slate-800 flex items-center justify-between gap-2 bg-slate-950/40 backdrop-blur sticky top-0 z-20">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onMenuClick}
          className="md:hidden tap-target -ml-1 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className={cn(
          'inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full ring-1 min-w-0',
          overall.tone === 'ok'       && 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
          overall.tone === 'warn'     && 'bg-yellow-500/10  text-yellow-300  ring-yellow-500/30',
          overall.tone === 'error'    && 'bg-orange-500/10  text-orange-300  ring-orange-500/30',
          overall.tone === 'critical' && 'bg-red-500/15     text-red-300     ring-red-500/40',
          overall.tone === 'info'     && 'bg-sky-500/10     text-sky-300     ring-sky-500/30',
        )}>
          <span className={cn('dot shrink-0',
            overall.tone === 'ok'       && 'bg-emerald-500',
            overall.tone === 'warn'     && 'bg-yellow-500',
            overall.tone === 'error'    && 'bg-orange-500',
            overall.tone === 'critical' && 'bg-red-500',
            overall.tone === 'info'     && 'bg-sky-500',
          )} />
          {/* "system:" label is desktop-only; phones just see the state word. */}
          <span className="truncate">
            <span className="hidden sm:inline">system: </span>{overall.text}
          </span>
        </div>
        {quietHours && (
          <span
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ring-1 bg-indigo-500/10 text-indigo-300 ring-indigo-500/30"
            title="Auto-fanout poller during 22:00–08:00 Europe/London emits only Exceptional dealTier deals; Strong/Standard re-evaluated at 08:00."
          >
            <span className="dot bg-indigo-400 shrink-0" />
            <span className="truncate">
              <span aria-hidden="true">🌙 </span>
              <span className="hidden sm:inline">quiet hours · Strong/Standard held</span>
              <span className="sm:hidden">quiet</span>
            </span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => refetch()}
          className="tap-target rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
        </button>
        <button
          onClick={() => { clearApiKey(); window.location.href = 'https://access.otrtech.co.uk/web/logout'; }}
          className="tap-target rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
}
